/**
 * S6.2 船務 capture domain — batch fields per Board 5 + the 裝櫃明細 read model.
 *
 * Extends the S6.1 `ShipmentBatch` contract with the Board 5 capture fields:
 * - batch level: mode 海運/空運/快遞, 櫃型 20GP/40HQ, 最後交期日, 批號 (nullable),
 *   Sample 資訊, and the 改數量 correction log
 * - line level: 工廠名稱/代碼 (factory = the PO vendor), 供應商出貨箱數/箱號
 *   range, 備註, plus the S6.1 材積 CBM (manual MVP + auto-from-dims where the
 *   product carries dimensions; Excel import spec is an open question — 林芊)
 *
 * SLICE-SCOPED TYPE HOME: `src/types/api/shipping.ts` is owned by a parallel
 * slice, so the extension interfaces live here and the MSW handlers import
 * them from this module. Fold into the API types file when the shipping type
 * seam is next open (recorded as a slice follow-up).
 *
 * Everything in this file is PURE (no React/antd) — the manifest grouping and
 * utilization math are shared verbatim by the UI and asserted by vitest, so
 * the drawer numbers can never drift from the roll-up rules.
 */

import type { ShipmentBatch, ShipmentBatchLine } from '@/types/api/shipping';

// ---------------------------------------------------------------------------
// Capture enums (labels are i18n copy — `shipping:batch.mode.*` etc.)
// ---------------------------------------------------------------------------

/** 運送方式 — 海運 sea / 空運 air / 快遞 express. */
export type ShipmentMode = 'sea' | 'air' | 'express';

/** 櫃型 — only meaningful for sea freight. */
export type ContainerType = '20GP' | '40HQ';

export const SHIPMENT_MODES: ShipmentMode[] = ['sea', 'air', 'express'];
export const CONTAINER_TYPES: ContainerType[] = ['20GP', '40HQ'];

/** Nominal internal capacity in CBM — drives the live utilization bar. */
export const CONTAINER_CAPACITY_CBM: Record<ContainerType, number> = {
  '20GP': 33.2,
  '40HQ': 76.4,
};

// ---------------------------------------------------------------------------
// Extended batch / line shapes
// ---------------------------------------------------------------------------

export interface ShipmentBatchLineCapture extends ShipmentBatchLine {
  /** 工廠名稱 — the PO vendor display name, stamped at batch create. */
  factoryName: string;
  /** 代碼 — the vendor code joined at batch create (manifest 代碼 column). */
  factoryCode: string | null;
  vendorId: number | null;
  /** 供應商出貨箱數 — carton count reported at loading. */
  cartonCount: number | null;
  /** 箱號 range start (numeric carton numbering, e.g. 1). */
  cartonFrom: number | null;
  /** 箱號 range end (e.g. 38). */
  cartonTo: number | null;
  /** 備註 — per-line manifest note. */
  note: string | null;
}

export interface BatchQuantityCorrectionChange {
  lineId: string;
  sku: string;
  qtyFrom: number;
  qtyTo: number;
}

/**
 * One 改數量 entry — the low-privilege correction loop for outsourced
 * loaders. Corrections never free-edit the sealed record: each one carries a
 * mandatory reason and the full before→after change set.
 */
export interface BatchQuantityCorrection {
  id: string;
  correctedAt: string;
  reason: string;
  changes: BatchQuantityCorrectionChange[];
}

export interface ShipmentBatchCapture extends Omit<ShipmentBatch, 'lines'> {
  mode: ShipmentMode;
  /** 櫃型 — null until chosen; always null for air/express. */
  containerType: ContainerType | null;
  /** 最後交期日 — the last date factories may deliver into this batch. */
  finalDeliveryDate: string | null;
  /** 批號 — nullable by contract (numbering scheme pending 林芊). */
  lotNumber: string | null;
  /** Sample 資訊 — free text riding along with the container. */
  sampleInfo: string | null;
  /** 改數量 audit log, oldest first. */
  corrections: BatchQuantityCorrection[];
  lines: ShipmentBatchLineCapture[];
}

// ---------------------------------------------------------------------------
// Widening — list surfaces still type rows as the S6.1 `ShipmentBatch`
// ---------------------------------------------------------------------------

/**
 * Widen an S6.1-typed batch to the capture shape with explicit defaults.
 * The mock API always returns the capture fields; this keeps the UI honest
 * (no bare `as` casts) until the API types file can absorb the extension.
 */
export function toCaptured(batch: ShipmentBatch): ShipmentBatchCapture {
  const partial = batch as ShipmentBatch & Partial<Omit<ShipmentBatchCapture, 'lines'>>;
  const lines = batch.lines as Array<ShipmentBatchLine & Partial<ShipmentBatchLineCapture>>;
  return {
    ...batch,
    mode: partial.mode ?? 'sea',
    containerType: partial.containerType ?? null,
    finalDeliveryDate: partial.finalDeliveryDate ?? null,
    lotNumber: partial.lotNumber ?? null,
    sampleInfo: partial.sampleInfo ?? null,
    corrections: partial.corrections ?? [],
    lines: lines.map((line) => ({
      ...line,
      factoryName: line.factoryName ?? '',
      factoryCode: line.factoryCode ?? null,
      vendorId: line.vendorId ?? null,
      cartonCount: line.cartonCount ?? null,
      cartonFrom: line.cartonFrom ?? null,
      cartonTo: line.cartonTo ?? null,
      note: line.note ?? null,
    })),
  };
}

// ---------------------------------------------------------------------------
// Roll-up math (ROUND_HALF_UP 2dp — house convention)
// ---------------------------------------------------------------------------

export function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Σ line CBM, 2dp. */
export function totalCbm(lines: Array<Pick<ShipmentBatchLine, 'cbm'>>): number {
  return round2(lines.reduce((sum, line) => sum + line.cbm, 0));
}

/**
 * Unit CBM derived from product mm dimensions (auto-from-dims path).
 * Null when any dimension is missing — the auto affordance hides then.
 */
export function unitCbmFromDims(product: {
  lengthMm?: number | null;
  widthMm?: number | null;
  heightMm?: number | null;
}): number | null {
  const { lengthMm, widthMm, heightMm } = product;
  if (!lengthMm || !widthMm || !heightMm) return null;
  return Math.round(((lengthMm * widthMm * heightMm) / 1_000_000_000) * 1_000_000) / 1_000_000;
}

/**
 * Container utilization in percent (1dp), or null when no capacity applies
 * (no 櫃型 chosen, or the batch moves by air/express).
 */
export function utilizationPct(
  cbm: number,
  containerType: ContainerType | null
): number | null {
  if (!containerType) return null;
  const capacity = CONTAINER_CAPACITY_CBM[containerType];
  return Math.round((cbm / capacity) * 1000) / 10;
}

// ---------------------------------------------------------------------------
// 裝櫃明細 manifest read model — the report SAMPLE panel as a screen
// ---------------------------------------------------------------------------

export interface ManifestFactoryRow {
  key: string;
  factoryCode: string | null;
  factoryName: string;
  /** min(cartonFrom) across the factory's lines — null when never reported. */
  cartonFrom: number | null;
  /** max(cartonTo) across the factory's lines. */
  cartonTo: number | null;
  /** Σ cartonCount — null when no line reported cartons. */
  cartonCount: number | null;
  cbm: number;
  notes: string[];
}

export interface ManifestTotals {
  cartonCount: number;
  cbm: number;
  factoryCount: number;
}

/**
 * Group batch lines per factory: 代碼 + 箱號範圍 + 箱數 + 備註 + 材積, plus the
 * TOTAL row figures. Pure — the drawer renders exactly this.
 */
export function buildManifest(lines: ShipmentBatchLineCapture[]): {
  rows: ManifestFactoryRow[];
  totals: ManifestTotals;
} {
  const rows: ManifestFactoryRow[] = [];
  const byKey = new Map<string, ManifestFactoryRow>();

  for (const line of lines) {
    const key = line.vendorId != null ? `vendor-${line.vendorId}` : `name-${line.factoryName}`;
    let row = byKey.get(key);
    if (!row) {
      row = {
        key,
        factoryCode: line.factoryCode,
        factoryName: line.factoryName,
        cartonFrom: null,
        cartonTo: null,
        cartonCount: null,
        cbm: 0,
        notes: [],
      };
      byKey.set(key, row);
      rows.push(row);
    }
    if (line.cartonCount != null) {
      row.cartonCount = (row.cartonCount ?? 0) + line.cartonCount;
    }
    if (line.cartonFrom != null) {
      row.cartonFrom = row.cartonFrom == null ? line.cartonFrom : Math.min(row.cartonFrom, line.cartonFrom);
    }
    if (line.cartonTo != null) {
      row.cartonTo = row.cartonTo == null ? line.cartonTo : Math.max(row.cartonTo, line.cartonTo);
    }
    row.cbm = round2(row.cbm + line.cbm);
    if (line.note) row.notes.push(line.note);
  }

  return {
    rows,
    totals: {
      cartonCount: rows.reduce((sum, row) => sum + (row.cartonCount ?? 0), 0),
      cbm: round2(rows.reduce((sum, row) => sum + row.cbm, 0)),
      factoryCount: rows.length,
    },
  };
}
